import { MDA_SEGMENTS } from "src/models/multidimensional-allocation/MultiAllocationSheetData";
import CONSTANTS from "src/utils/constants";

// Form data interface
export type UploadSheetFormState = {
    name: string;
    description: string;
    segmentType: string;
    allocationSheets: File[];
    errors: {[k in keyof UploadSheetFormState]?: string};
}

export type SegmentTypeOption = {
    label: string;
    value: string;
}

export enum UploadSheetActionTypes {
    SET_NAME = 'SET_NAME',
    SET_DESCRIPTION = 'SET_DESCRIPTION',
    SET_SEGMENT_TYPE = 'SET_SEGMENT_TYPE',
    SET_ALLOCATION_SHEETS = 'SET_ALLOCATION_SHEETS',
    SET_FIELD_ERROR = 'SET_FIELD_ERROR',
    SET_ERRORS = 'SET_ERRORS',
    CLEAR_ERRORS = 'CLEAR_ERRORS',
    RESET_FORM = 'RESET_FORM'
}

// Initial state
export const INITIAL_STATE: UploadSheetFormState = {
    name: '',
    description: '',
    segmentType: MDA_SEGMENTS.COST_CENTER,
    allocationSheets: [],
    errors: {},
};

// LG Code (segment 8) is offered only when the backend GetCOATypes LOV includes it —
// the backend filters that LOV on the Secondary Ledger feature flag. Mirrors the
// calculation-builder pattern ("backend gates availability via feature flag").
// All other segments are always available.
export const getSegmentTypeOptions = (backendCoaTypes: string[]): SegmentTypeOption[] =>
    Object.values(MDA_SEGMENTS)
        .filter(val => val !== MDA_SEGMENTS.LG_CODE
            || backendCoaTypes.includes(CONSTANTS.COA_SEGMENT_MAPPING.LG_CODE.UI))
        .map(val => ({
            label: val,
            value: val
        }));

// Validation function for name field (allows alphanumeric, underscores, spaces, and dashes)
function validateName(name: string) {
    if (!name.trim()) {
        return 'Name is required';
    }
    if (!/^[a-zA-Z0-9_ -]+$/.test(name)) {
        return 'Name must only contain letters, numbers, underscores, spaces, and dashes';
    }
    return undefined;
};

function validateDescription(description: string) {
    if(!description.trim()) {
        return 'Description is required';
    }

    return undefined;
}
 
function validateFile(files: File[]) {

    if(files.length === 0) {
        return `Please upload a file`;
    }
    const file = files[0];
    const fileName = file.name.toLowerCase();
    const validExtensions = ['.xls', '.xlsx'];
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!hasValidExtension) {
        return `File "${file.name}" must have .xls or .xlsx extension`;
    }
    return undefined
}

// Validated against the same LOV-derived options the dropdown renders, so a segment the user can
// actually pick is never rejected. Validating against a fixed list instead would reject LG Code
// wherever the backend LOV offers it, and an unloaded LOV still fails closed because the option
// list is empty of LG Code too.
function validateSegmentType(segmentType: string, segmentTypeOptions: SegmentTypeOption[]) {
    
    const isValid = segmentTypeOptions.some((segment) => segment.value === segmentType)

    return !isValid ? `Invalid segment type: ${segmentType}` : undefined;
}

export function validateState(
    state: UploadSheetFormState, segmentTypeOptions: SegmentTypeOption[]
): {[k in keyof UploadSheetFormState]?: string} {

    return {
        name: validateName(state.name),
        description: validateDescription(state.description),
        segmentType: validateSegmentType(state.segmentType, segmentTypeOptions),
        allocationSheets: validateFile(state.allocationSheets)
    }
}

// Discriminated union types for actions
type UploadSheetAction = 
    | { type: UploadSheetActionTypes.SET_NAME; name: string }
    | { type: UploadSheetActionTypes.SET_DESCRIPTION; description: string }
    | { type: UploadSheetActionTypes.SET_SEGMENT_TYPE; segmentType: string; segmentTypeOptions: SegmentTypeOption[] }
    | { type: UploadSheetActionTypes.SET_ALLOCATION_SHEETS; allocationSheets: File[] }
    | { type: UploadSheetActionTypes.SET_FIELD_ERROR; error: string; fieldName: keyof UploadSheetFormState }
    | { type: UploadSheetActionTypes.CLEAR_ERRORS }
    | { type: UploadSheetActionTypes.SET_ERRORS; errors: {[k in keyof UploadSheetFormState]?: string}}
    | { type: UploadSheetActionTypes.RESET_FORM };

export function uploadSheetFormReducer(state: UploadSheetFormState, action: UploadSheetAction): UploadSheetFormState {
    switch (action.type) {
        case UploadSheetActionTypes.SET_NAME:
            return {
                ...state,
                name: action.name,
                errors: {
                    ...state.errors,
                    name: validateName(action.name)
                }
            };
        
        case UploadSheetActionTypes.SET_DESCRIPTION:
            return {
                ...state,
                description: action.description,
                errors: {
                    ...state.errors,
                    description: validateDescription(action.description)
                }
            };
        
        case UploadSheetActionTypes.SET_SEGMENT_TYPE:
            return {
                ...state,
                segmentType: action.segmentType,
                errors: {
                    ...state.errors,
                    segmentType: validateSegmentType(action.segmentType, action.segmentTypeOptions)
                }
            };
        
        case UploadSheetActionTypes.SET_ALLOCATION_SHEETS:
            return {
                ...state,
                allocationSheets: action.allocationSheets,
                errors: {
                    ...state.errors,
                    allocationSheets: validateFile(action.allocationSheets)
                }
            };
        
        case UploadSheetActionTypes.SET_FIELD_ERROR:
            return {
                ...state,
                errors: {
                    ...state.errors,
                    [action.fieldName]: action.error
                }
            };
        case UploadSheetActionTypes.SET_ERRORS:
            return {
                ...state,
                errors: {
                    ...state.errors,
                    ...action.errors
                }
            }
        case UploadSheetActionTypes.CLEAR_ERRORS:
            return {
                ...state,
                errors: {}
            };
          
        case UploadSheetActionTypes.RESET_FORM:
            return INITIAL_STATE;
        
        default:
            console.warn(`No action found for ${action}. Returning unchanged state`);
            return state;
    }
}
